import Phaser from 'phaser';

/**
 * TextureGenerator
 * Dynamically generates 16-bit style pixel art placeholder textures using HTML5 Canvas / Phaser Graphics.
 * This guarantees 100% playable placeholder assets without relying on missing external image files,
 * allowing developers to swap files in `src/Assets/` cleanly later.
 */
export class TextureGenerator {
  public static generateAll(scene: Phaser.Scene): void {
    this.createPlayerTexture(scene);
    this.createEnemyTextures(scene);
    this.createNPCTexture(scene);
    this.createCollectibleTextures(scene);
    this.createCheckpointTexture(scene);
    this.createPortalTextures(scene);
    this.createEnvironmentTiles(scene);
    this.createInteractiveTextures(scene);
    this.createBackgroundTextures(scene);
    this.createParticleTextures(scene);
  }

  private static createPlayerTexture(scene: Phaser.Scene): void {
    if (scene.textures.exists('player')) return;
    const canvas = scene.textures.createCanvas('player', 32, 32);
    if (!canvas) return;
    const ctx = canvas.getContext();

    // Body - Blue Square with bevels
    ctx.fillStyle = '#2563eb';
    ctx.fillRect(2, 2, 28, 28);
    ctx.fillStyle = '#60a5fa';
    ctx.fillRect(4, 4, 24, 4); // Highlight
    ctx.fillStyle = '#1d4ed8';
    ctx.fillRect(4, 24, 24, 4); // Shadow

    // Face / Eyes
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(8, 10, 6, 8);
    ctx.fillRect(18, 10, 6, 8);
    ctx.fillStyle = '#0f172a';
    ctx.fillRect(11, 12, 3, 4);
    ctx.fillRect(21, 12, 3, 4);

    // Border
    ctx.strokeStyle = '#1e3a8a';
    ctx.lineWidth = 2;
    ctx.strokeRect(1, 1, 30, 30);
    canvas.refresh();
  }

  private static createEnemyTextures(scene: Phaser.Scene): void {
    // Red Ground Enemy
    if (!scene.textures.exists('enemy_ground')) {
      const canvas = scene.textures.createCanvas('enemy_ground', 32, 32);
      if (canvas) {
        const ctx = canvas.getContext();
        ctx.fillStyle = '#dc2626';
        ctx.fillRect(2, 2, 28, 28);
        ctx.fillStyle = '#f87171';
        ctx.fillRect(4, 4, 24, 4);
        ctx.fillStyle = '#991b1b';
        ctx.fillRect(4, 24, 24, 4);

        // Angry Eyes
        ctx.fillStyle = '#fef08a';
        ctx.fillRect(6, 10, 8, 4);
        ctx.fillRect(18, 10, 8, 4);
        ctx.fillStyle = '#7f1d1d';
        ctx.fillRect(10, 11, 4, 3);
        ctx.fillRect(18, 11, 4, 3);

        ctx.strokeStyle = '#450a0a';
        ctx.lineWidth = 2;
        ctx.strokeRect(1, 1, 30, 30);
        canvas.refresh();
      }
    }

    // Purple Flying Enemy (Circle)
    if (!scene.textures.exists('enemy_flying')) {
      const canvas = scene.textures.createCanvas('enemy_flying', 32, 32);
      if (canvas) {
        const ctx = canvas.getContext();
        ctx.fillStyle = '#9333ea';
        ctx.beginPath();
        ctx.arc(16, 16, 14, 0, Math.PI * 2);
        ctx.fill();

        ctx.fillStyle = '#c084fc';
        ctx.beginPath();
        ctx.arc(14, 12, 10, 0, Math.PI * 2);
        ctx.fill();

        // Wings
        ctx.fillStyle = '#e9d5ff';
        ctx.fillRect(0, 12, 6, 8);
        ctx.fillRect(26, 12, 6, 8);

        // Glowing eye
        ctx.fillStyle = '#ec4899';
        ctx.fillRect(12, 12, 8, 8);
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(14, 14, 4, 4);

        canvas.refresh();
      }
    }

    // Cyan Drone (Hexagon)
    if (!scene.textures.exists('enemy_drone')) {
      const canvas = scene.textures.createCanvas('enemy_drone', 32, 32);
      if (canvas) {
        const ctx = canvas.getContext();
        ctx.fillStyle = '#0891b2';
        ctx.beginPath();
        ctx.moveTo(16, 2);
        ctx.lineTo(28, 9);
        ctx.lineTo(28, 23);
        ctx.lineTo(16, 30);
        ctx.lineTo(4, 23);
        ctx.lineTo(4, 9);
        ctx.closePath();
        ctx.fill();

        ctx.fillStyle = '#67e8f9';
        ctx.fillRect(12, 12, 8, 8);
        canvas.refresh();
      }
    }

    // Stone Golem (Large Dark Gray/Red miniboss)
    if (!scene.textures.exists('enemy_golem')) {
      const canvas = scene.textures.createCanvas('enemy_golem', 64, 64);
      if (canvas) {
        const ctx = canvas.getContext();
        ctx.fillStyle = '#374151';
        ctx.fillRect(4, 4, 56, 56);
        ctx.fillStyle = '#9ca3af';
        ctx.fillRect(8, 8, 48, 8);
        ctx.fillStyle = '#ef4444';
        ctx.fillRect(16, 24, 12, 8);
        ctx.fillRect(36, 24, 12, 8);
        ctx.strokeStyle = '#111827';
        ctx.lineWidth = 4;
        ctx.strokeRect(2, 2, 60, 60);
        canvas.refresh();
      }
    }
  }

  private static createNPCTexture(scene: Phaser.Scene): void {
    if (scene.textures.exists('npc')) return;
    const canvas = scene.textures.createCanvas('npc', 32, 32);
    if (!canvas) return;
    const ctx = canvas.getContext();

    ctx.fillStyle = '#16a34a';
    ctx.fillRect(2, 2, 28, 28);
    ctx.fillStyle = '#4ade80';
    ctx.fillRect(4, 4, 24, 4);

    // Friendly Face
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(8, 10, 4, 6);
    ctx.fillRect(20, 10, 4, 6);
    ctx.fillStyle = '#15803d';
    ctx.fillRect(10, 20, 12, 3); // Smile

    ctx.strokeStyle = '#052e16';
    ctx.lineWidth = 2;
    ctx.strokeRect(1, 1, 30, 30);
    canvas.refresh();
  }

  private static createCollectibleTextures(scene: Phaser.Scene): void {
    // Coin (Yellow Circle)
    if (!scene.textures.exists('coin')) {
      const canvas = scene.textures.createCanvas('coin', 16, 16);
      if (canvas) {
        const ctx = canvas.getContext();
        ctx.fillStyle = '#eab308';
        ctx.beginPath();
        ctx.arc(8, 8, 7, 0, Math.PI * 2);
        ctx.fill();
        ctx.fillStyle = '#fef08a';
        ctx.beginPath();
        ctx.arc(6, 6, 4, 0, Math.PI * 2);
        ctx.fill();
        ctx.strokeStyle = '#a16207';
        ctx.lineWidth = 1;
        ctx.strokeRect(6, 6, 4, 4);
        canvas.refresh();
      }
    }

    // Gem (Blue Diamond)
    if (!scene.textures.exists('gem')) {
      const canvas = scene.textures.createCanvas('gem', 20, 24);
      if (canvas) {
        const ctx = canvas.getContext();
        ctx.fillStyle = '#0284c7';
        ctx.beginPath();
        ctx.moveTo(10, 2);
        ctx.lineTo(18, 8);
        ctx.lineTo(10, 22);
        ctx.lineTo(2, 8);
        ctx.closePath();
        ctx.fill();

        ctx.fillStyle = '#38bdf8';
        ctx.beginPath();
        ctx.moveTo(10, 2);
        ctx.lineTo(18, 8);
        ctx.lineTo(10, 12);
        ctx.closePath();
        ctx.fill();

        ctx.fillStyle = '#e0f2fe';
        ctx.fillRect(8, 4, 4, 4);
        canvas.refresh();
      }
    }
  }

  private static createCheckpointTexture(scene: Phaser.Scene): void {
    if (scene.textures.exists('checkpoint')) return;
    const canvas = scene.textures.createCanvas('checkpoint', 32, 48);
    if (!canvas) return;
    const ctx = canvas.getContext();

    // Base pedestal
    ctx.fillStyle = '#475569';
    ctx.fillRect(4, 36, 24, 12);

    // Cyan Crystal
    ctx.fillStyle = '#06b6d4';
    ctx.beginPath();
    ctx.moveTo(16, 4);
    ctx.lineTo(26, 20);
    ctx.lineTo(16, 36);
    ctx.lineTo(6, 20);
    ctx.closePath();
    ctx.fill();

    ctx.fillStyle = '#a5f3fc';
    ctx.beginPath();
    ctx.moveTo(16, 4);
    ctx.lineTo(26, 20);
    ctx.lineTo(16, 20);
    ctx.closePath();
    ctx.fill();

    canvas.refresh();
  }

  private static createPortalTextures(scene: Phaser.Scene): void {
    // Blue Main Teleport Portal
    if (!scene.textures.exists('portal_blue')) {
      const canvas = scene.textures.createCanvas('portal_blue', 48, 64);
      if (canvas) {
        const ctx = canvas.getContext();
        ctx.fillStyle = '#1e40af';
        ctx.fillRect(0, 0, 48, 64);
        ctx.fillStyle = '#3b82f6';
        ctx.fillRect(6, 6, 36, 52);
        ctx.fillStyle = '#60a5fa';
        ctx.fillRect(12, 12, 24, 40);
        ctx.fillStyle = '#dbeafe';
        ctx.fillRect(18, 18, 12, 28);
        canvas.refresh();
      }
    }

    // Purple Secret Room Portal
    if (!scene.textures.exists('portal_purple')) {
      const canvas = scene.textures.createCanvas('portal_purple', 48, 64);
      if (canvas) {
        const ctx = canvas.getContext();
        ctx.fillStyle = '#581c87';
        ctx.fillRect(0, 0, 48, 64);
        ctx.fillStyle = '#a855f7';
        ctx.fillRect(6, 6, 36, 52);
        ctx.fillStyle = '#c084fc';
        ctx.fillRect(12, 12, 24, 40);
        ctx.fillStyle = '#f3e8ff';
        ctx.fillRect(18, 18, 12, 28);
        canvas.refresh();
      }
    }
  }

  private static createEnvironmentTiles(scene: Phaser.Scene): void {
    const tileSize = 32;

    // Ground Tile (Gray with top Moss)
    if (!scene.textures.exists('tile_ground')) {
      const canvas = scene.textures.createCanvas('tile_ground', tileSize, tileSize);
      if (canvas) {
        const ctx = canvas.getContext();
        ctx.fillStyle = '#4b5563';
        ctx.fillRect(0, 0, tileSize, tileSize);
        // Moss top
        ctx.fillStyle = '#15803d';
        ctx.fillRect(0, 0, tileSize, 6);
        ctx.fillStyle = '#22c55e';
        ctx.fillRect(4, 6, 6, 4);
        ctx.fillRect(18, 6, 8, 3);
        // Stone texture detail
        ctx.fillStyle = '#374151';
        ctx.fillRect(4, 14, 10, 8);
        ctx.fillRect(18, 20, 10, 6);
        canvas.refresh();
      }
    }

    // Stone Tile (Dark Gray)
    if (!scene.textures.exists('tile_stone')) {
      const canvas = scene.textures.createCanvas('tile_stone', tileSize, tileSize);
      if (canvas) {
        const ctx = canvas.getContext();
        ctx.fillStyle = '#374151';
        ctx.fillRect(0, 0, tileSize, tileSize);
        ctx.strokeStyle = '#1f2937';
        ctx.lineWidth = 2;
        ctx.strokeRect(1, 1, 30, 30);
        ctx.fillStyle = '#4b5563';
        ctx.fillRect(4, 4, 12, 10);
        ctx.fillRect(18, 16, 10, 12);
        canvas.refresh();
      }
    }

    // Wood Tile (Brown)
    if (!scene.textures.exists('tile_wood')) {
      const canvas = scene.textures.createCanvas('tile_wood', tileSize, tileSize);
      if (canvas) {
        const ctx = canvas.getContext();
        ctx.fillStyle = '#78350f';
        ctx.fillRect(0, 0, tileSize, tileSize);
        ctx.fillStyle = '#92400e';
        ctx.fillRect(0, 2, tileSize, 6);
        ctx.fillRect(0, 14, tileSize, 6);
        ctx.fillRect(0, 26, tileSize, 4);
        ctx.strokeStyle = '#451a03';
        ctx.lineWidth = 1;
        ctx.strokeRect(0, 0, tileSize, tileSize);
        canvas.refresh();
      }
    }

    // Crystal Tile (Blue Transparent)
    if (!scene.textures.exists('tile_crystal')) {
      const canvas = scene.textures.createCanvas('tile_crystal', tileSize, tileSize);
      if (canvas) {
        const ctx = canvas.getContext();
        ctx.fillStyle = 'rgba(14, 165, 233, 0.75)';
        ctx.fillRect(0, 0, tileSize, tileSize);
        ctx.fillStyle = '#e0f2fe';
        ctx.beginPath();
        ctx.moveTo(8, 4); ctx.lineTo(16, 16); ctx.lineTo(12, 28);
        ctx.fill();
        canvas.refresh();
      }
    }

    // Breakable Orange Tile
    if (!scene.textures.exists('tile_breakable')) {
      const canvas = scene.textures.createCanvas('tile_breakable', tileSize, tileSize);
      if (canvas) {
        const ctx = canvas.getContext();
        ctx.fillStyle = '#ea580c';
        ctx.fillRect(0, 0, tileSize, tileSize);
        ctx.strokeStyle = '#7c2d12';
        ctx.lineWidth = 2;
        ctx.beginPath();
        ctx.moveTo(4, 4); ctx.lineTo(14, 16); ctx.lineTo(28, 28);
        ctx.stroke();
        canvas.refresh();
      }
    }

    // Ice Platform Tile (Light Blue Slippery)
    if (!scene.textures.exists('tile_ice')) {
      const canvas = scene.textures.createCanvas('tile_ice', tileSize, tileSize);
      if (canvas) {
        const ctx = canvas.getContext();
        ctx.fillStyle = '#93c5fd';
        ctx.fillRect(0, 0, tileSize, tileSize);
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(2, 2, 28, 4);
        canvas.refresh();
      }
    }

    // Lava Hazard Tile
    if (!scene.textures.exists('tile_lava')) {
      const canvas = scene.textures.createCanvas('tile_lava', tileSize, tileSize);
      if (canvas) {
        const ctx = canvas.getContext();
        ctx.fillStyle = '#b91c1c';
        ctx.fillRect(0, 0, tileSize, tileSize);
        ctx.fillStyle = '#f97316';
        ctx.fillRect(4, 4, 24, 12);
        ctx.fillStyle = '#fef08a';
        ctx.fillRect(8, 6, 8, 6);
        canvas.refresh();
      }
    }
  }

  private static createInteractiveTextures(scene: Phaser.Scene): void {
    // Moving Platform (Dark metal)
    if (!scene.textures.exists('platform_moving')) {
      const canvas = scene.textures.createCanvas('platform_moving', 96, 20);
      if (canvas) {
        const ctx = canvas.getContext();
        ctx.fillStyle = '#1e293b';
        ctx.fillRect(0, 0, 96, 20);
        ctx.fillStyle = '#06b6d4';
        ctx.fillRect(6, 8, 12, 4);
        ctx.fillRect(78, 8, 12, 4);
        ctx.strokeStyle = '#475569';
        ctx.lineWidth = 2;
        ctx.strokeRect(1, 1, 94, 18);
        canvas.refresh();
      }
    }

    // Switch (Yellow Button)
    if (!scene.textures.exists('switch_off')) {
      const canvas = scene.textures.createCanvas('switch_off', 32, 16);
      if (canvas) {
        const ctx = canvas.getContext();
        ctx.fillStyle = '#475569';
        ctx.fillRect(4, 10, 24, 6);
        ctx.fillStyle = '#eab308';
        ctx.fillRect(10, 2, 12, 8);
        canvas.refresh();
      }
    }

    if (!scene.textures.exists('switch_on')) {
      const canvas = scene.textures.createCanvas('switch_on', 32, 16);
      if (canvas) {
        const ctx = canvas.getContext();
        ctx.fillStyle = '#475569';
        ctx.fillRect(4, 10, 24, 6);
        ctx.fillStyle = '#22c55e';
        ctx.fillRect(10, 6, 12, 4);
        canvas.refresh();
      }
    }

    // Metallic Door
    if (!scene.textures.exists('door_closed')) {
      const canvas = scene.textures.createCanvas('door_closed', 32, 64);
      if (canvas) {
        const ctx = canvas.getContext();
        ctx.fillStyle = '#334155';
        ctx.fillRect(0, 0, 32, 64);
        ctx.strokeStyle = '#94a3b8';
        ctx.lineWidth = 2;
        ctx.strokeRect(2, 2, 28, 60);
        ctx.fillStyle = '#eab308';
        ctx.fillRect(22, 30, 6, 6);
        canvas.refresh();
      }
    }

    // Castle Structure (Large Ending Exit)
    if (!scene.textures.exists('castle_structure')) {
      const canvas = scene.textures.createCanvas('castle_structure', 192, 192);
      if (canvas) {
        const ctx = canvas.getContext();
        // Castle stone body
        ctx.fillStyle = '#1e293b';
        ctx.fillRect(0, 0, 192, 192);
        // Battlement crenellations
        ctx.fillStyle = '#0f172a';
        for (let x = 0; x < 192; x += 32) {
          ctx.fillRect(x, 0, 16, 24);
        }
        // Archway Door opening
        ctx.fillStyle = '#020617';
        ctx.beginPath();
        ctx.arc(96, 192, 48, Math.PI, 0);
        ctx.fill();
        ctx.fillRect(48, 144, 96, 48);

        // Banners
        ctx.fillStyle = '#dc2626';
        ctx.fillRect(32, 40, 20, 60);
        ctx.fillRect(140, 40, 20, 60);
        canvas.refresh();
      }
    }

    // Bounce Flower / Spring
    if (!scene.textures.exists('bounce_flower')) {
      const canvas = scene.textures.createCanvas('bounce_flower', 32, 32);
      if (canvas) {
        const ctx = canvas.getContext();
        ctx.fillStyle = '#15803d';
        ctx.fillRect(14, 16, 4, 16);
        ctx.fillStyle = '#ec4899';
        ctx.beginPath();
        ctx.arc(16, 12, 12, 0, Math.PI * 2);
        ctx.fill();
        ctx.fillStyle = '#fef08a';
        ctx.beginPath();
        ctx.arc(16, 12, 5, 0, Math.PI * 2);
        ctx.fill();
        canvas.refresh();
      }
    }

    // Giant Mushroom
    if (!scene.textures.exists('giant_mushroom')) {
      const canvas = scene.textures.createCanvas('giant_mushroom', 64, 64);
      if (canvas) {
        const ctx = canvas.getContext();
        ctx.fillStyle = '#fef3c7';
        ctx.fillRect(24, 24, 16, 40);
        ctx.fillStyle = '#dc2626';
        ctx.beginPath();
        ctx.arc(32, 28, 28, Math.PI, 0);
        ctx.fill();
        ctx.fillStyle = '#ffffff';
        ctx.beginPath(); ctx.arc(20, 16, 5, 0, Math.PI * 2); ctx.fill();
        ctx.beginPath(); ctx.arc(44, 16, 5, 0, Math.PI * 2); ctx.fill();
        canvas.refresh();
      }
    }

    // Vine
    if (!scene.textures.exists('vine')) {
      const canvas = scene.textures.createCanvas('vine', 16, 32);
      if (canvas) {
        const ctx = canvas.getContext();
        ctx.fillStyle = '#16a34a';
        ctx.fillRect(6, 0, 4, 32);
        ctx.fillStyle = '#22c55e';
        ctx.fillRect(2, 6, 6, 4);
        ctx.fillRect(8, 20, 6, 4);
        canvas.refresh();
      }
    }

    // Rotating Gear
    if (!scene.textures.exists('gear')) {
      const canvas = scene.textures.createCanvas('gear', 48, 48);
      if (canvas) {
        const ctx = canvas.getContext();
        ctx.fillStyle = '#64748b';
        ctx.beginPath();
        ctx.arc(24, 24, 20, 0, Math.PI * 2);
        ctx.fill();
        ctx.fillStyle = '#0f172a';
        ctx.beginPath();
        ctx.arc(24, 24, 8, 0, Math.PI * 2);
        ctx.fill();
        canvas.refresh();
      }
    }
  }

  private static createBackgroundTextures(scene: Phaser.Scene): void {
    // Parallax Mountain Layer
    if (!scene.textures.exists('bg_mountains')) {
      const canvas = scene.textures.createCanvas('bg_mountains', 512, 256);
      if (canvas) {
        const ctx = canvas.getContext();
        ctx.fillStyle = '#1e1b4b';
        ctx.beginPath();
        ctx.moveTo(0, 256);
        ctx.lineTo(80, 100);
        ctx.lineTo(160, 200);
        ctx.lineTo(280, 60);
        ctx.lineTo(400, 180);
        ctx.lineTo(512, 90);
        ctx.lineTo(512, 256);
        ctx.closePath();
        ctx.fill();
        canvas.refresh();
      }
    }

    // Parallax Forest Layer
    if (!scene.textures.exists('bg_forest')) {
      const canvas = scene.textures.createCanvas('bg_forest', 512, 256);
      if (canvas) {
        const ctx = canvas.getContext();
        ctx.fillStyle = '#064e3b';
        for (let x = 0; x < 512; x += 40) {
          ctx.beginPath();
          ctx.moveTo(x, 256);
          ctx.lineTo(x + 20, 140);
          ctx.lineTo(x + 40, 256);
          ctx.fill();
        }
        canvas.refresh();
      }
    }

    // Parallax Floating Islands Layer
    if (!scene.textures.exists('bg_islands')) {
      const canvas = scene.textures.createCanvas('bg_islands', 512, 256);
      if (canvas) {
        const ctx = canvas.getContext();
        ctx.fillStyle = '#312e81';
        ctx.beginPath();
        ctx.ellipse(100, 80, 60, 20, 0, 0, Math.PI * 2);
        ctx.ellipse(360, 110, 80, 25, 0, 0, Math.PI * 2);
        ctx.fill();
        canvas.refresh();
      }
    }
  }

  private static createParticleTextures(scene: Phaser.Scene): void {
    if (!scene.textures.exists('particle_star')) {
      const canvas = scene.textures.createCanvas('particle_star', 8, 8);
      if (canvas) {
        const ctx = canvas.getContext();
        ctx.fillStyle = '#fef08a';
        ctx.fillRect(2, 0, 4, 8);
        ctx.fillRect(0, 2, 8, 4);
        canvas.refresh();
      }
    }

    if (!scene.textures.exists('particle_dust')) {
      const canvas = scene.textures.createCanvas('particle_dust', 6, 6);
      if (canvas) {
        const ctx = canvas.getContext();
        ctx.fillStyle = '#cbd5e1';
        ctx.fillRect(0, 0, 6, 6);
        canvas.refresh();
      }
    }
  }
}
